import { useMemo, useState } from 'react';
import type { Color, GameState, Move, Square } from '@ccc/shared-types';
import { Piece } from './Pieces';
import { cn } from '../lib/classnames';

interface ChessBoardProps {
  state: GameState;
  /** Side viewed from the player's perspective (white on bottom by default). */
  orientation?: Color | undefined;
  /** Side allowed to move; null = view-only. */
  playableSide: Color | null;
  legalMovesFor?: ((square: Square) => Move[]) | undefined;
  onMove?: ((move: Move) => void) | undefined;
  highlightSquares?: Square[] | undefined;
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const RANKS = [8, 7, 6, 5, 4, 3, 2, 1] as const;

export function ChessBoard({
  state,
  orientation = 'white',
  playableSide,
  legalMovesFor,
  onMove,
  highlightSquares = [],
}: ChessBoardProps) {
  const [selected, setSelected] = useState<Square | null>(null);

  const orderedFiles = orientation === 'white' ? FILES : [...FILES].reverse();
  const orderedRanks = orientation === 'white' ? RANKS : [...RANKS].reverse();

  const pieceMap = useMemo(() => {
    const m: Record<string, GameState['pieces'][string]> = {};
    for (const p of Object.values(state.pieces)) m[p.square] = p;
    return m;
  }, [state.pieces]);

  const selectedMoves = useMemo(() => {
    if (!selected || !legalMovesFor) return [];
    return legalMovesFor(selected);
  }, [selected, legalMovesFor]);

  const targetSquares = new Set(selectedMoves.map((m) => m.to));
  const captureSquares = new Set(selectedMoves.filter((m) => m.capturedPieceId).map((m) => m.to));

  function squareClick(sq: Square) {
    if (selected && targetSquares.has(sq)) {
      const move = selectedMoves.find((m) => m.to === sq);
      if (move) onMove?.(move);
      setSelected(null);
      return;
    }
    const piece = pieceMap[sq];
    if (piece && piece.color === playableSide) {
      setSelected(sq);
    } else {
      setSelected(null);
    }
  }

  return (
    <div
      className="grid border-2 border-ink/20 rounded-md overflow-hidden shadow-card select-none"
      style={{ gridTemplateColumns: 'repeat(8, 1fr)', aspectRatio: '1 / 1' }}
    >
      {orderedRanks.map((rank) =>
        orderedFiles.map((file) => {
          const sq = `${file}${rank}` as Square;
          const isLight = (FILES.indexOf(file) + rank) % 2 === 1;
          const piece = pieceMap[sq];
          const isSelected = selected === sq;
          const isTarget = targetSquares.has(sq);
          const isCapture = captureSquares.has(sq);
          const isHighlight = highlightSquares.includes(sq);
          return (
            <button
              key={sq}
              type="button"
              onClick={() => squareClick(sq)}
              className={cn(
                'relative aspect-square w-full grid place-items-center transition focus-ring',
                isLight ? 'bg-board-light' : 'bg-board-dark',
                isSelected && 'ring-2 ring-accent ring-inset',
                isHighlight && 'ring-2 ring-accent ring-inset',
              )}
              aria-label={`${sq}${piece ? ` ${piece.color} ${piece.type}` : ''}`}
            >
              {/* Coordinate labels at the edges */}
              {file === orderedFiles[0] && (
                <span
                  className={cn(
                    'absolute top-0.5 left-1 text-[10px] font-mono leading-none pointer-events-none',
                    isLight ? 'text-ink/40' : 'text-board-light/60',
                  )}
                >
                  {rank}
                </span>
              )}
              {rank === orderedRanks[orderedRanks.length - 1] && (
                <span
                  className={cn(
                    'absolute bottom-0.5 right-1 text-[10px] font-mono leading-none pointer-events-none',
                    isLight ? 'text-ink/40' : 'text-board-light/60',
                  )}
                >
                  {file}
                </span>
              )}
              {/* Move-target dot */}
              {isTarget && !piece && (
                <span
                  className="block h-3 w-3 rounded-full bg-accent/55 pointer-events-none"
                  aria-hidden
                />
              )}
              {/* Capture ring */}
              {isCapture && piece && (
                <span
                  className="absolute inset-1 rounded-full border-2 border-accent/70 pointer-events-none"
                  aria-hidden
                />
              )}
              {piece && (
                <span className="relative" style={{ width: '82%', height: '82%' }}>
                  <Piece type={piece.type} color={piece.color} size={64} className="w-full h-full" />
                </span>
              )}
            </button>
          );
        }),
      )}
    </div>
  );
}
